import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GetIRLDocData } from '../redux/documentsSlice';
import {
    buildIrlCitationDetail,
    buildObservationCitationDetail,
    getCitationDisplayTitle,
    hasOpenableIrlCitation,
    hasOpenableObservationCitation,
    isIrlCitation,
    isObservationCitation,
} from '../utils/citationDisplay';
import { canOpenLensCitationPdf, findCitationByRef, getLensCitationChunk } from '../utils/lensIntelligenceCitations';
import { projectLevelDataroomPath } from '../utils/intelligenceSourceReadiness';

export function useLensIntelligenceCitationPanels({
    modalFiles = [],
    effectiveWorkstreamIds = [],
    citationDisplayCtx = {},
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [citationPanel, setCitationPanel] = useState(null);
    const [irlCitationPanel, setIrlCitationPanel] = useState(null);
    const [observationCitationPanel, setObservationCitationPanel] = useState(null);
    const [panelExpanded, setPanelExpanded] = useState(false);

    const sidePanelOpen = Boolean(citationPanel || irlCitationPanel || observationCitationPanel);

    useEffect(() => {
        if (!sidePanelOpen) setPanelExpanded(false);
    }, [sidePanelOpen]);

    const closeAllPanels = useCallback(() => {
        setCitationPanel(null);
        setIrlCitationPanel(null);
        setObservationCitationPanel(null);
        setPanelExpanded(false);
    }, []);

    const togglePanelExpand = useCallback(() => {
        setPanelExpanded(prev => !prev);
    }, []);

    const openIrlCitationPanel = useCallback(
        (c, linkedChunk) => {
            setCitationPanel(null);
            setObservationCitationPanel(null);
            const detail = buildIrlCitationDetail(c, linkedChunk, citationDisplayCtx);
            setIrlCitationPanel({
                detail,
                token: `irl-${detail.irl_ref || detail.irl_id || c?.ref || 'cite'}-${Date.now()}`,
            });
        },
        [citationDisplayCtx],
    );

    const openObservationCitationPanel = useCallback((c, linkedChunk) => {
        setCitationPanel(null);
        setIrlCitationPanel(null);
        const detail = buildObservationCitationDetail(c, linkedChunk);
        setObservationCitationPanel({
            detail,
            token: `obs-${detail.observation_ref || detail.observation_id || c?.ref || 'cite'}-${Date.now()}`,
        });
    }, []);

    const handleIrlCitationClick = useCallback(
        (c, linkedChunk) => {
            if (!hasOpenableIrlCitation(c, linkedChunk)) {
                toast.error('This IRL citation has no detail metadata to show.');
                return;
            }
            openIrlCitationPanel(c, linkedChunk);
        },
        [openIrlCitationPanel],
    );

    const handleObservationCitationClick = useCallback(
        (c, linkedChunk) => {
            if (!hasOpenableObservationCitation(c, linkedChunk)) {
                toast.error('This observation citation has no detail metadata to show.');
                return;
            }
            openObservationCitationPanel(c, linkedChunk);
        },
        [openObservationCitationPanel],
    );

    const handleCitationClick = useCallback(
        async (c, sourceChunks = []) => {
            const linkedChunk = getLensCitationChunk(c, sourceChunks);
            if (isObservationCitation(c, linkedChunk)) {
                handleObservationCitationClick(c, linkedChunk);
                return;
            }
            if (isIrlCitation(c, linkedChunk)) {
                handleIrlCitationClick(c, linkedChunk);
                return;
            }

            const projectPath = projectLevelDataroomPath(c) || projectLevelDataroomPath(linkedChunk || {});
            if (projectPath) {
                closeAllPanels();
                navigate(projectPath);
                return;
            }

            const fid = c?.file_id;
            if (fid == null) return toast.error('This citation has no file id.');
            const resolvedWsId = c?.workstream_id ?? effectiveWorkstreamIds[0];
            if (resolvedWsId == null) return toast.error('This citation has no workstream context.');

            let doc = modalFiles.find(f => String(f.id) === String(fid));
            if (!doc?.document) {
                try {
                    const res = await dispatch(GetIRLDocData({ id: fid, workstream: Number(resolvedWsId) })).unwrap();
                    doc = res?.data;
                } catch {
                    return toast.error('Could not load this file for viewing.');
                }
            }
            if (!doc?.document) return toast.error('This file has no PDF or blob to open in the viewer.');

            const chunkText = linkedChunk?.chunk || '';
            const pageRaw = c?.page_number ?? c?.page;
            let pageOneBased = null;
            if (pageRaw != null && `${pageRaw}` !== '') {
                const n = Number(pageRaw);
                if (!Number.isNaN(n)) pageOneBased = n + 1;
            }

            setIrlCitationPanel(null);
            setObservationCitationPanel(null);
            setCitationPanel({
                doc,
                highlightText: chunkText,
                pageOneBased,
                token: `${doc.id}-${c.chunk_id ?? c.ref ?? 'cite'}-${Date.now()}`,
            });
        },
        [modalFiles, dispatch, effectiveWorkstreamIds, handleIrlCitationClick, handleObservationCitationClick, closeAllPanels, navigate],
    );

    const handleInlineCitationClick = useCallback(
        (ref, citation, linkedChunk, sourceChunks, citations = []) => {
            const c = citation || findCitationByRef(citations, ref);
            const chunk = linkedChunk ?? getLensCitationChunk(c, sourceChunks);
            if (c && isObservationCitation(c, chunk) && hasOpenableObservationCitation(c, chunk)) {
                openObservationCitationPanel(c, chunk);
                return;
            }
            if (c && isIrlCitation(c, chunk) && hasOpenableIrlCitation(c, chunk)) {
                openIrlCitationPanel(c, chunk);
                return;
            }
            if (c) {
                void handleCitationClick(c, sourceChunks);
            }
        },
        [openIrlCitationPanel, openObservationCitationPanel, handleCitationClick],
    );

    const openCitationFromEvidenceItem = useCallback(
        (item, citations = [], chunks = []) => {
            const byRef = item?.ref != null ? findCitationByRef(citations, item.ref) : null;
            if (byRef) {
                void handleCitationClick(byRef, chunks);
                return;
            }
            const title = item?.title || item?.filename;
            if (!title) return;
            const match = (citations || []).find(c => {
                const linked = getLensCitationChunk(c, chunks);
                return getCitationDisplayTitle(c, linked, citationDisplayCtx) === title;
            });
            if (match) {
                void handleCitationClick(match, chunks);
            }
        },
        [handleCitationClick, citationDisplayCtx],
    );

    const canOpenCitationPdf = useCallback(
        c => canOpenLensCitationPdf(c, effectiveWorkstreamIds),
        [effectiveWorkstreamIds],
    );

    const panelProps = useMemo(
        () => ({
            citationPanel,
            irlCitationPanel,
            observationCitationPanel,
            setCitationPanel,
            setIrlCitationPanel,
            setObservationCitationPanel,
            panelExpanded,
            togglePanelExpand,
        }),
        [citationPanel, irlCitationPanel, observationCitationPanel, panelExpanded, togglePanelExpand],
    );

    return {
        sidePanelOpen,
        panelExpanded,
        togglePanelExpand,
        closeAllPanels,
        handleCitationClick,
        handleIrlCitationClick,
        handleObservationCitationClick,
        handleInlineCitationClick,
        openCitationFromEvidenceItem,
        canOpenCitationPdf,
        panelProps,
    };
}
